import {
  CharacterMetadata,
  ContentBlock,
  ContentState,
} from 'draft-js'
import { is, List, OrderedSet } from 'immutable'
import { ENTITY_TYPE } from '@disco/constants/content-editor'
import { exportMergeTag } from './editor-utils'
const BLOCK_TYPE = {
  // This is used to represent a normal text block (paragraph).
  HEADER_ONE: 'header-one',
  HEADER_TWO: 'header-two',
  HEADER_THREE: 'header-three',
  HEADER_FOUR: 'header-four',
  HEADER_FIVE: 'header-five',
  HEADER_SIX: 'header-six',
  UNORDERED_LIST_ITEM: 'unordered-list-item',
  ORDERED_LIST_ITEM: 'ordered-list-item',
  BLOCKQUOTE: 'blockquote',
  CODE: 'code-block',
  ATOMIC: 'atomic',
}
const INLINE_STYLE = {
  BOLD: 'BOLD',
  CODE: 'CODE',
  ITALIC: 'ITALIC',
  STRIKETHROUGH: 'STRIKETHROUGH',
  UNDERLINE: 'UNDERLINE',
}
type EntityKey = string | null | undefined
type Style = OrderedSet<string>
type StyleRange = [string, Style]
type EntityRange = [EntityKey, Array<StyleRange>]
type CharacterMetaList = List<CharacterMetadata>
const EMPTY_SET: Style = OrderedSet<string>()

function getEntityRanges(
  text: string,
  charMetaList: CharacterMetaList,
): Array<EntityRange> {
  let charEntity: string | null | undefined = null
  let prevCharEntity: string | null | undefined = null
  const ranges: Array<EntityRange> = []
  let rangeStart = 0

  for (let i = 0, len = text.length; i < len; i++) {
    prevCharEntity = charEntity
    const meta: CharacterMetadata = charMetaList.get(i)
    charEntity = meta ? meta.getEntity() : null

    if (i > 0 && charEntity !== prevCharEntity) {
      ranges.push([
        prevCharEntity,
        getStyleRanges(
          text.slice(rangeStart, i),
          charMetaList.slice(rangeStart, i),
        ),
      ])
      rangeStart = i
    }
  }

  ranges.push([
    charEntity,
    getStyleRanges(text.slice(rangeStart), charMetaList.slice(rangeStart)),
  ])
  return ranges
}

function getStyleRanges(
  text: string,
  charMetaList: CharacterMetaList,
): Array<StyleRange> {
  let charStyle = EMPTY_SET
  let prevCharStyle = EMPTY_SET
  const ranges: StyleRange[] = []
  let rangeStart = 0

  for (let i = 0, len = text.length; i < len; i++) {
    prevCharStyle = charStyle
    const meta = charMetaList.get(i)
    charStyle = meta ? meta.getStyle() : EMPTY_SET

    if (i > 0 && !is(charStyle, prevCharStyle)) {
      ranges.push([text.slice(rangeStart, i), prevCharStyle])
      rangeStart = i
    }
  }

  ranges.push([text.slice(rangeStart), charStyle])
  return ranges
}

const { BOLD } = INLINE_STYLE
const CODE_INDENT = '    '

class MarkupGenerator {
  blocks: Array<ContentBlock>
  contentState: ContentState
  currentBlock: number
  output: Array<string>
  totalBlocks: number
  listItemCounts: Record<string, any>

  constructor(contentState: ContentState) {
    this.contentState = contentState
  }

  generate(): string {
    this.output = []
    this.blocks = this.contentState.getBlockMap().toArray()
    this.totalBlocks = this.blocks.length
    this.currentBlock = 0
    this.listItemCounts = {}

    while (this.currentBlock < this.totalBlocks) {
      this.processBlock()
    }

    return this.output.join('')
  }

  processBlock() {
    const block = this.blocks[this.currentBlock]
    const blockType = block.getType()

    switch (blockType) {
      case BLOCK_TYPE.HEADER_ONE:
      case BLOCK_TYPE.HEADER_TWO:
      case BLOCK_TYPE.HEADER_THREE:
      case BLOCK_TYPE.HEADER_FOUR:
      case BLOCK_TYPE.HEADER_FIVE:
      case BLOCK_TYPE.HEADER_SIX:
        this.insertLineBreaks()
        this.output.push(getHeader(this.renderBlockContent(block)))
        break

      case BLOCK_TYPE.UNORDERED_LIST_ITEM: {
        const blockDepth = block.getDepth()
        const lastBlock = this.getLastBlock()
        const lastBlockType = lastBlock ? lastBlock.getType() : null
        const lastBlockDepth =
          lastBlock && canHaveDepth(lastBlockType) ? lastBlock.getDepth() : null

        if (lastBlockType !== blockType && lastBlockDepth !== blockDepth - 1) {
          this.insertLineBreaks()

          // Insert an additional line break if following opposite list type.
          if (lastBlockType === BLOCK_TYPE.ORDERED_LIST_ITEM) {
            this.insertLineBreaks()
          }
        }

        const indent = ' '.repeat(block.depth * 4)
        this.output.push(indent + '- ' + this.renderBlockContent(block) + '\n')
        break
      }

      case BLOCK_TYPE.ORDERED_LIST_ITEM: {
        const blockDepth = block.getDepth()
        const lastBlock = this.getLastBlock()
        const lastBlockType = lastBlock ? lastBlock.getType() : null
        const lastBlockDepth =
          lastBlock && canHaveDepth(lastBlockType) ? lastBlock.getDepth() : null

        if (lastBlockType !== blockType && lastBlockDepth !== blockDepth - 1) {
          this.insertLineBreaks()

          // Insert an additional line break if following opposite list type.
          if (lastBlockType === BLOCK_TYPE.UNORDERED_LIST_ITEM) {
            this.insertLineBreaks()
          }
        }

        const indent = ' '.repeat(blockDepth * 4)
        // TODO: figure out what to do with two-digit numbers
        const count = this.getListItemCount(block) % 10
        this.output.push(
          indent + `${count}. ` + this.renderBlockContent(block) + '\n',
        )
        break
      }

      case BLOCK_TYPE.BLOCKQUOTE: {
        this.insertLineBreaks()
        this.output.push(' > ' + this.renderBlockContent(block) + '\n')
        break
      }

      case BLOCK_TYPE.CODE: {
        this.insertLineBreaks()
        this.output.push(CODE_INDENT + this.renderBlockContent(block) + '\n')
        break
      }

      default: {
        // this.insertLineBreaks()
        this.output.push(this.renderBlockContent(block) + '\n')
        break
      }
    }

    this.currentBlock += 1
  }

  getLastBlock(): ContentBlock {
    return this.blocks[this.currentBlock - 1]
  }

  getNextBlock(): ContentBlock {
    return this.blocks[this.currentBlock + 1]
  }

  getListItemCount(block: ContentBlock): number {
    const blockType = block.getType()
    const blockDepth = block.getDepth()
    // To decide if we need to start over we need to backtrack (skipping list
    // items that are of greater depth)
    let index = this.currentBlock - 1
    let prevBlock = this.blocks[index]

    while (
      prevBlock &&
      canHaveDepth(prevBlock.getType()) &&
      prevBlock.getDepth() > blockDepth
    ) {
      index -= 1
      prevBlock = this.blocks[index]
    }

    if (
      !prevBlock ||
      prevBlock.getType() !== blockType ||
      prevBlock.getDepth() !== blockDepth
    ) {
      this.listItemCounts[blockDepth] = 0
    }

    return (this.listItemCounts[blockDepth] =
      this.listItemCounts[blockDepth] + 1)
  }

  insertLineBreaks() {
    if (this.currentBlock > 0) {
      this.output.push('\n')
    }
  }

  renderBlockContent(block: ContentBlock): string {
    const { contentState } = this
    const text = block.getText()

    if (text === '') {
      // Prevent element collapse if completely empty.
      // TODO: Replace with constant.
      return '\u200B'
    }

    const charMetaList = block.getCharacterList()
    const entityPieces = getEntityRanges(text, charMetaList)
    return entityPieces
      .map(([entityKey, stylePieces]) => {
        const content = stylePieces
          .map(([text, style]) => {
            // Don't allow empty inline elements.
            if (!text) {
              return ''
            }

            let content = text

            if (style.has(BOLD)) {
              content = `*${content}*`
            }

            return content
          })
          .join('')
        const entity = entityKey ? contentState.getEntity(entityKey) : null

        if (entity != null && entity.getType() === ENTITY_TYPE.LINK) {
          const data = entity.getData()
          const url = data.url || ''
          return `${content} (${url})`
        } else if (entity != null && entity.getType() === ENTITY_TYPE.IMAGE) {
          return ''
        } else if (entity != null && entity.getType() === 'button') {
          const data = entity.getData()
          const url = data.link || ''
          return `>> ${content}\n${url}`
        } else if (
          entity != null &&
          entity.getType() === ENTITY_TYPE.MERGE_TAG
        ) {
          return exportMergeTag(entity)
        } else {
          return content
        }
      })
      .join('')
  }
}

function canHaveDepth(blockType: any): boolean {
  switch (blockType) {
    case BLOCK_TYPE.UNORDERED_LIST_ITEM:
    case BLOCK_TYPE.ORDERED_LIST_ITEM:
      return true

    default:
      return false
  }
}

export const getStringOfChars = (char: string, number: number): string =>
  char.repeat(number)
export const getDividerElement = (): string => getStringOfChars('=', 60)
export const getHeader = (header: string): string =>
  `** ${header}\n${getStringOfChars('-', 60)}\n`
export default function stateToText(content: ContentState): string {
  return new MarkupGenerator(content).generate()
}
